import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, where, doc, getDoc } from '../firebaseConfig';
import { Bar } from 'react-chartjs-2';
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import Divider from "@mui/material/Divider";
import AssessmentIcon from '@mui/icons-material/Assessment';
import MDTypography from "components/MDTypography";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

const EquityGraph = ({ userID, tradeAccountID = 'default' }) => {
  const [trades, setTrades] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [accountName, setAccountName] = useState(tradeAccountID); // Default to ID
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

    // Fetch account name on mount
    useEffect(() => {
      const fetchAccountName = async () => {
        if (tradeAccountID === 'default') {
          setAccountName('Default');
          return;
        }
        try {
          const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
          if (accountDoc.exists()) {
            setAccountName(accountDoc.data().name || tradeAccountID);
          }
        } catch (error) {
          console.error('Error fetching account name:', error);
        }
      };
      fetchAccountName();
    }, [userID, tradeAccountID]);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', 'Closed')
        );
        const tradesSnapshot = await getDocs(tradesQuery);
        const tradesData = tradesSnapshot.docs.map(doc => ({
          id: doc.id,
          accountID: tradeAccountID,
          ...doc.data()
        }));
        setTrades(tradesData);
      } catch (error) {
        console.error('Error fetching trades for EquityGraph:', error);
      }
    };

    fetchTrades();
  }, [userID, tradeAccountID]);

  const calculateEquity = (trades, year) => {
    const monthlyEquity = Array(12).fill(0);

    trades.forEach(trade => {
      const tradeDate = new Date(trade.date);
      if (tradeDate.getFullYear() === year && trade.status === 'Closed') {
        const month = tradeDate.getMonth();
        monthlyEquity[month] += parseFloat(trade.returns);
      }
    });

    return monthlyEquity;
  };

  const monthlyEquity = calculateEquity(trades, year);

  const data = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Equity Profit/Loss',
        data: monthlyEquity,
        backgroundColor: monthlyEquity.map(value => 
          value >= 0 
            ? theme.palette.success.main + '40' // Green with 40% opacity for profits
            : theme.palette.error.main + '40'   // Red with 40% opacity for losses
        ),
        borderColor: monthlyEquity.map(value => 
          value >= 0 
            ? theme.palette.success.main 
            : theme.palette.error.main
        ),
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return `$${value}`;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Profit/Loss: $${context.parsed.y.toFixed(2)}`;
          }
        }
      }
    }
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const tooltipContent = `The Equity Profit/Loss Analysis displays monthly profit/loss totals, with green bars indicating profitable months and red bars showing losses. Hover over any bar to view detailed stats for that month. Use the year selector to compare performance across years and track monthly trends.`;

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <AssessmentIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Monthly Equity Profit/Loss {tradeAccountID === 'default' ? '' : `(${accountName})`}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  }
                }
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="center" mt={2}>
          <Select value={year} onChange={handleYearChange}>
            {[2024, 2025, 2026].map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </MDBox>
        <MDBox height="100%">
          <Bar data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

EquityGraph.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default EquityGraph;